<template>
  <div>
    <RctNavbar :links="navLinks" logo-href="https://ridgecodetech.com">
    </RctNavbar>

    <!-- HERO -->
    <section class="hero">
      <div class="hero-content">
        <p class="hero-eyebrow">Contract Engineering · Athens, AL</p>
        <h1 class="hero-headline">Senior engineering,<br><em>on your timeline.</em></h1>
        <p class="hero-sub">Contract software development and technical consulting for teams that need reliable, well-engineered work — without the overhead of a large agency.</p>
        <div class="hero-actions">
          <RctButton href="#contact">Start a Project</RctButton>
          <RctButton variant="ghost" href="#services">View Services →</RctButton>
        </div>
      </div>
      <div class="ridge-divider">
        <svg viewBox="0 0 1130 120" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
          <rect x="0" y="80" width="1130" height="40" fill="#f5f8ff"/>
          <path d="M0,120 L0,90 C60,88 120,85 180,82 C220,80 260,74 300,72 C340,70 360,68 390,64 C420,60 445,55 470,52 C490,49 505,50 520,53 C538,57 552,62 568,65 C590,69 615,70 640,68 C665,66 690,60 715,56 C740,52 760,50 785,52 C810,54 830,58 855,62 C880,66 905,70 930,73 C960,77 990,80 1020,83 C1060,87 1090,90 1130,92 L1130,120 Z" fill="#f5f8ff"/>
          <path d="M0,90 C60,88 120,85 180,82 C220,80 260,74 300,72 C340,70 360,68 390,64 C420,60 445,55 470,52 C490,49 505,50 520,53 C538,57 552,62 568,65 C590,69 615,70 640,68 C665,66 690,60 715,56 C740,52 760,50 785,52 C810,54 830,58 855,62 C880,66 905,70 930,73 C960,77 990,80 1020,83 C1060,87 1090,90 1130,92" fill="none" stroke="#becdea" stroke-width="1.5" opacity="0.6"/>
        </svg>
      </div>
    </section>

    <!-- SERVICES -->
    <section class="services" id="services">
      <div class="section-inner">
        <p class="section-label reveal">What I Offer</p>
        <h2 class="section-heading reveal reveal-delay-1">Purpose-built engineering<br>for real problems.</h2>
        <div class="services-grid">
          <a class="service-card reveal reveal-delay-1" href="https://custom.ridgecodetech.com">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="6" width="32" height="22" rx="2" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M12 18l4 4 8-8" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <h3 class="service-title">Custom Software Development</h3>
            <p class="service-desc">Full-stack web apps, APIs, and backend systems built to spec. From greenfield projects to feature additions on existing codebases — no shortcuts.</p>
          </a>
          <a class="service-card reveal reveal-delay-2" href="https://ai.ridgecodetech.com">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="18" cy="18" r="6" stroke="#7a96c1" stroke-width="1.5"/>
              <path d="M18 4v4M18 28v4M4 18h4M28 18h4" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M8.2 8.2l2.8 2.8M25 25l2.8 2.8M8.2 27.8l2.8-2.8M25 11l2.8-2.8" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <h3 class="service-title">Systems &amp; Performance Engineering</h3>
            <p class="service-desc">High-throughput backends and performance-critical components in Rust. Reliable, fast, and memory-safe — especially where it matters most.</p>
          </a>
          <a class="service-card reveal reveal-delay-3" href="https://consulting.ridgecodetech.com">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 10h24M6 18h16M6 26h20" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            <h3 class="service-title">Technical Consulting</h3>
            <p class="service-desc">Architecture reviews, technology decisions, and engineering strategy. Senior-level advice on stack, design, and tradeoffs — without agency overhead.</p>
          </a>
          <a class="service-card reveal reveal-delay-4" href="https://web.ridgecodetech.com">
            <svg class="service-icon" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="4" y="4" width="12" height="12" rx="1.5" stroke="#7a96c1" stroke-width="1.5"/>
              <rect x="20" y="4" width="12" height="12" rx="1.5" stroke="#7a96c1" stroke-width="1.5"/>
              <rect x="4" y="20" width="12" height="12" rx="1.5" stroke="#7a96c1" stroke-width="1.5"/>
              <rect x="20" y="20" width="12" height="12" rx="1.5" stroke="#7a96c1" stroke-width="1.5"/>
            </svg>
            <h3 class="service-title">Full-Stack Web Applications</h3>
            <p class="service-desc">React, Vue, and Node.js applications for small businesses, startups, and internal tools. From MVP to production, end to end.</p>
          </a>
        </div>
      </div>
    </section>

    <!-- ENGAGEMENT MODELS -->
    <section class="engagement-section" id="engagement">
      <div class="section-inner">
        <p class="section-label reveal">How We Work Together</p>
        <h2 class="section-heading reveal reveal-delay-1">Flexible engagements<br>built around your needs.</h2>
        <p class="section-body reveal reveal-delay-2">Every project is different. Here's how most engagements are structured — reach out and we'll find the right fit.</p>
        <div class="engagement-grid reveal reveal-delay-3">
          <div class="engagement-card">
            <div class="engagement-icon">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="8" width="32" height="26" rx="2" stroke="#7a96c1" stroke-width="1.5"/>
                <path d="M4 16h32" stroke="#7a96c1" stroke-width="1.5"/>
                <path d="M13 24h14M13 29h8" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </div>
            <h3 class="engagement-title">Project-Based</h3>
            <p class="engagement-desc">Fixed scope, clear deliverables, milestone billing. Best for well-defined projects with defined outcomes — greenfield builds, feature sprints, or specific integrations.</p>
            <span class="engagement-tag">Most common</span>
          </div>
          <div class="engagement-card">
            <div class="engagement-icon">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="14" stroke="#7a96c1" stroke-width="1.5"/>
                <path d="M20 12v8l5 3" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <h3 class="engagement-title">Time &amp; Materials</h3>
            <p class="engagement-desc">Hourly or weekly-retainer work. Best for ongoing feature development, maintenance, or when scope is still being defined. Transparent reporting included.</p>
            <span class="engagement-tag">Flexible</span>
          </div>
          <div class="engagement-card">
            <div class="engagement-icon">
              <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 30L20 12l12 18" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M12.5 24h15" stroke="#7a96c1" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </div>
            <h3 class="engagement-title">Technical Advisory</h3>
            <p class="engagement-desc">Architecture reviews, technology evaluations, and strategy sessions. Delivered as written documents, structured reports, or working sessions. Priced per engagement.</p>
            <span class="engagement-tag">Per engagement</span>
          </div>
        </div>
        <p class="engagement-note reveal">Rates are available on request. <a href="#contact">Get in touch</a> and we'll find a structure that works.</p>
      </div>
    </section>

    <!-- ABOUT -->
    <section class="about" id="about">
      <div class="section-inner">
        <p class="section-label reveal">About</p>
        <h2 class="section-heading reveal reveal-delay-1">Engineering with context.</h2>
        <div class="about-layout">
          <div class="about-text reveal reveal-delay-2">
            <p>RidgeCodeTech LLC is an independent software consultancy based in Athens, Alabama. I work with small businesses, startups, and development teams who need focused engineering help — without the overhead of a large agency.</p>
            <p>My background spans <strong>high-responsibility environments</strong> — military service, crisis work, and years of leading people under pressure — before software. I bring that same reliability and communication standard to every engagement.</p>
            <p>I take on a limited number of engagements at once. That means focused attention, clear communication, and work that actually ships.</p>
          </div>
          <div class="about-stats reveal reveal-delay-3">
            <div class="stat-box">
              <div class="stat-number">BS</div>
              <div class="stat-label">Software Engineering, completed</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">MS</div>
              <div class="stat-label">Computer Science in progress</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">Rust</div>
              <div class="stat-label">Primary language for systems work</div>
            </div>
            <div class="stat-box">
              <div class="stat-number">Full</div>
              <div class="stat-label">Stack — front to back to infra</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CONTACT -->
    <section class="contact-section" id="contact">
      <div class="section-inner">
        <p class="section-label reveal">Get In Touch</p>
        <h2 class="section-heading reveal reveal-delay-1">Ready to start a project?</h2>
        <div class="contact-layout">
          <div class="contact-aside reveal reveal-delay-2">
            <p>I take on a limited number of consulting engagements at a time. Tell me what you're building and I'll get back to you within one business day.</p>
            <div class="contact-detail">
              <a href="mailto:hello@ridgecodetech.com">hello@ridgecodetech.com</a>
              <a href="https://ridgecodetech.com">ridgecodetech.com</a>
            </div>
          </div>
          <div class="reveal reveal-delay-3">
            <form class="contact-form" novalidate @submit.prevent="handleSubmit">
              <div class="form-row">
                <div class="form-group">
                  <label for="name">Name</label>
                  <input v-model="formName" type="text" id="name" name="name" placeholder="Your name" required />
                </div>
                <div class="form-group">
                  <label for="email">Email</label>
                  <input v-model="formEmail" type="email" id="email" name="email" placeholder="you@company.com" required />
                </div>
              </div>
              <div class="form-group">
                <label for="service">Service</label>
                <select v-model="formService" id="service" name="service" required>
                  <option value="" disabled>What do you need help with?</option>
                  <option value="Custom Software Development">Custom Software Development</option>
                  <option value="Systems & Performance Engineering">Systems &amp; Performance Engineering (Rust)</option>
                  <option value="Technical Consulting">Technical Consulting / Architecture Review</option>
                  <option value="Full-Stack Web Application">Full-Stack Web Application</option>
                  <option value="Retainer Engagement">Ongoing Retainer Engagement</option>
                  <option value="Other">Other / Not sure yet</option>
                </select>
              </div>
              <div class="form-group">
                <label for="message">Message</label>
                <textarea v-model="formMessage" id="message" name="message" placeholder="Tell me about your project, timeline, budget range, and any constraints..." required></textarea>
              </div>
              <div v-if="formStatus.msg" :class="['form-status', formStatus.type]">{{ formStatus.msg }}</div>
              <RctButton type="submit" :loading="formLoading" :disabled="formLoading">Send Message</RctButton>
            </form>
          </div>
        </div>
      </div>
    </section>

    <AgentChat scope="consulting" :agent-url="AGENT_URL" />

    <RctFooter :links="footerLinks">
    </RctFooter>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { RctNavbar, RctButton, RctFooter, AgentChat } from '@rct/ui';
import { FOOTER_LINKS, WORKER_URL, AGENT_URL, isValidEmail } from '@rct/shared';

const navLinks = [
  { href: '#services',   label: 'Services'   },
  { href: '#engagement', label: 'Engagement' },
  { href: '#about',      label: 'About'      },
  { href: '#contact',    label: 'Contact'    },
  { href: '/sign-in',    label: 'Client Login' },
];

const footerLinks = [
  { href: 'https://ridgecodetech.com', label: 'Home' },
  ...FOOTER_LINKS.filter((l) => l.href !== 'https://consulting.ridgecodetech.com'),
] as { href: string; label: string }[];

const formName    = ref('');
const formEmail   = ref('');
const formService = ref('');
const formMessage = ref('');
const formLoading = ref(false);
const formStatus  = ref<{ type: 'success' | 'error' | ''; msg: string }>({ type: '', msg: '' });

async function handleSubmit() {
  const name    = formName.value.trim();
  const email   = formEmail.value.trim();
  const service = formService.value;
  const message = formMessage.value.trim();

  if (!name || !email || !service || !message) {
    formStatus.value = { type: 'error', msg: 'Please fill in all fields before sending.' };
    return;
  }
  if (!isValidEmail(email)) {
    formStatus.value = { type: 'error', msg: 'Please enter a valid email address.' };
    return;
  }

  formLoading.value = true;
  formStatus.value  = { type: '', msg: '' };

  try {
    const res = await fetch(WORKER_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ name, email, service, message }),
    });

    if (res.ok) {
      formStatus.value = { type: 'success', msg: "Message sent — I'll be in touch within one business day." };
      formName.value = formEmail.value = formService.value = formMessage.value = '';
    } else {
      const data = await res.json().catch(() => ({})) as { error?: string };
      formStatus.value = { type: 'error', msg: data.error ?? 'Something went wrong. Please try emailing directly.' };
    }
  } catch {
    formStatus.value = { type: 'error', msg: 'Network error — please email hello@ridgecodetech.com directly.' };
  } finally {
    formLoading.value = false;
  }
}

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );
  document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
});
</script>

<style>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --navy:       #0f264f;
  --navy-deep:  #091a38;
  --navy-mid:   #1a3a6e;
  --periwinkle: #becdea;
  --steel:      #7a96c1;
  --pale:       #e1ecff;
  --off-white:  #f5f8ff;
  --white:      #ffffff;
  --text-body:  #2c3e5c;
  --text-muted: #6b82a0;
}

html { scroll-behavior: smooth; }

body {
  font-family: 'Inter', sans-serif;
  background: var(--off-white);
  color: var(--text-body);
  overflow-x: hidden;
}

/* SCROLL REVEAL */
.reveal {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.65s cubic-bezier(.4,0,.2,1), transform 0.65s cubic-bezier(.4,0,.2,1);
}
.reveal.visible { opacity: 1; transform: none; }
.reveal-delay-1 { transition-delay: 0.1s; }
.reveal-delay-2 { transition-delay: 0.2s; }
.reveal-delay-3 { transition-delay: 0.3s; }
.reveal-delay-4 { transition-delay: 0.4s; }

/* HERO */
.hero {
  min-height: 100vh;
  background: var(--navy-deep);
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 10rem 4rem 0;
  position: relative;
  overflow: hidden;
}
.hero::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background:
    radial-gradient(ellipse 60% 50% at 70% 30%, rgba(122,150,193,0.12) 0%, transparent 70%),
    radial-gradient(ellipse 40% 40% at 20% 70%, rgba(15,38,79,0.8) 0%, transparent 60%);
  pointer-events: none;
}
.hero-content { position: relative; max-width: 820px; }
.hero-eyebrow {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--steel);
  margin-bottom: 1.5rem;
  opacity: 0;
  animation: fadeUp 0.7s 0.2s cubic-bezier(.4,0,.2,1) forwards;
}
.hero-headline {
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2.8rem, 6vw, 5rem);
  font-weight: 700;
  line-height: 1.08;
  color: var(--white);
  letter-spacing: -0.02em;
  margin-bottom: 1.75rem;
  opacity: 0;
  animation: fadeUp 0.7s 0.4s cubic-bezier(.4,0,.2,1) forwards;
}
.hero-headline em { font-style: normal; color: var(--periwinkle); }
.hero-sub {
  font-size: 1.15rem;
  font-weight: 300;
  color: var(--periwinkle);
  line-height: 1.7;
  max-width: 540px;
  margin-bottom: 3rem;
  opacity: 0;
  animation: fadeUp 0.7s 0.6s cubic-bezier(.4,0,.2,1) forwards;
}
.hero-actions {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  opacity: 0;
  animation: fadeUp 0.7s 0.75s cubic-bezier(.4,0,.2,1) forwards;
}
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: none; }
}

/* RIDGE DIVIDER */
.ridge-divider {
  position: relative;
  width: 100%;
  height: 120px;
  background: var(--navy-deep);
  margin-top: 6rem;
}
.ridge-divider svg { position: absolute; bottom: 0; left: 0; width: 100%; height: 100%; }

/* SECTION BASE */
section { padding: 6rem 4rem; }
.section-inner { max-width: 1100px; margin: 0 auto; }
.section-label {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--steel);
  margin-bottom: 1rem;
}
.section-heading {
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--navy);
  line-height: 1.2;
  margin-bottom: 1.5rem;
}
.section-body {
  font-size: 1rem;
  font-weight: 300;
  color: var(--text-muted);
  line-height: 1.8;
  max-width: 600px;
  margin-bottom: 0;
}

/* SERVICES */
.services { background: var(--off-white); }
.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 2px;
  margin-top: 3.5rem;
  border: 2px solid var(--periwinkle);
}
.service-card {
  display: block;
  padding: 2.5rem 2rem;
  background: var(--white);
  border-right: 2px solid var(--periwinkle);
  transition: background 0.2s;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
}
.service-card:last-child { border-right: none; }
.service-card:hover { background: var(--pale); }
.service-icon { width: 36px; height: 36px; margin-bottom: 1.25rem; opacity: 0.7; }
.service-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--navy);
  margin-bottom: 0.75rem;
}
.service-desc { font-size: 0.875rem; line-height: 1.7; color: var(--text-muted); font-weight: 300; }

/* ENGAGEMENT MODELS */
.engagement-section { background: var(--navy); }
.engagement-section .section-heading { color: var(--pale); }
.engagement-section .section-body { color: var(--periwinkle); margin-bottom: 3rem; }
.engagement-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  border: 2px solid var(--navy-mid);
  margin-top: 3rem;
}
.engagement-card {
  background: rgba(255,255,255,0.03);
  border-right: 2px solid var(--navy-mid);
  padding: 2.5rem 2rem;
  transition: background 0.2s;
}
.engagement-card:last-child { border-right: none; }
.engagement-card:hover { background: rgba(122,150,193,0.07); }
.engagement-icon { width: 40px; height: 40px; margin-bottom: 1.25rem; opacity: 0.7; }
.engagement-title {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  color: var(--pale);
  margin-bottom: 0.75rem;
}
.engagement-desc { font-size: 0.875rem; line-height: 1.7; color: var(--periwinkle); font-weight: 300; margin-bottom: 1.25rem; }
.engagement-tag {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--steel);
  border: 1px solid var(--navy-mid);
  padding: 0.3rem 0.65rem;
  border-radius: 2px;
}
.engagement-note {
  margin-top: 2.5rem;
  font-size: 0.9rem;
  color: var(--periwinkle);
  font-weight: 300;
}
.engagement-note a {
  color: var(--steel);
  text-decoration: none;
  border-bottom: 1px solid var(--navy-mid);
  padding-bottom: 1px;
  transition: color 0.2s, border-color 0.2s;
}
.engagement-note a:hover { color: var(--pale); border-color: var(--steel); }

/* ABOUT */
.about { background: var(--off-white); }
.about-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;
  margin-top: 3rem;
}
.about-text p { font-size: 1rem; line-height: 1.85; color: var(--text-muted); font-weight: 300; margin-bottom: 1.25rem; }
.about-text p:last-child { margin-bottom: 0; }
.about-text strong { color: var(--navy); font-weight: 500; }
.about-stats {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5px;
  background: var(--periwinkle);
  border: 1.5px solid var(--periwinkle);
}
.stat-box { background: var(--white); padding: 2rem 1.75rem; transition: background 0.2s; }
.stat-box:hover { background: var(--pale); }
.stat-number {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 2.25rem;
  font-weight: 700;
  color: var(--navy);
  letter-spacing: -0.03em;
  line-height: 1;
  margin-bottom: 0.5rem;
}
.stat-label { font-size: 0.8rem; color: var(--text-muted); font-weight: 400; line-height: 1.4; }

/* CONTACT */
.contact-section { background: var(--navy-deep); padding: 7rem 4rem; }
.contact-section .section-label { color: var(--steel); }
.contact-section .section-heading { color: var(--pale); }
.contact-layout {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 5rem;
  align-items: start;
  margin-top: 3rem;
}
.contact-aside p {
  font-size: 0.9rem;
  color: var(--periwinkle);
  line-height: 1.8;
  font-weight: 300;
  margin-bottom: 2rem;
}
.contact-detail { display: flex; flex-direction: column; gap: 0.75rem; }
.contact-detail a {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.875rem;
  color: var(--steel);
  text-decoration: none;
  border-bottom: 1px solid var(--navy-mid);
  padding-bottom: 2px;
  width: fit-content;
  transition: color 0.2s, border-color 0.2s;
}
.contact-detail a:hover { color: var(--pale); border-color: var(--steel); }
.contact-form { display: flex; flex-direction: column; gap: 1.25rem; }
.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
.form-group { display: flex; flex-direction: column; gap: 0.4rem; }
.form-group label {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: var(--steel);
}
.form-group input,
.form-group select,
.form-group textarea {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 300;
  color: var(--pale);
  background: rgba(255,255,255,0.05);
  border: 1px solid var(--navy-mid);
  border-radius: 3px;
  padding: 0.8rem 1rem;
  outline: none;
  transition: border-color 0.2s, background 0.2s;
  width: 100%;
}
.form-group select option { background: var(--navy-deep); color: var(--pale); }
.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus { border-color: var(--steel); background: rgba(255,255,255,0.08); }
.form-group textarea { resize: vertical; min-height: 130px; }
.form-group input::placeholder,
.form-group textarea::placeholder { color: var(--text-muted); }
.form-status {
  font-size: 0.875rem;
  padding: 0.75rem 1rem;
  border-radius: 3px;
}
.form-status.success { background: rgba(122,150,193,0.15); border: 1px solid var(--steel); color: var(--pale); }
.form-status.error   { background: rgba(180,60,60,0.15);  border: 1px solid #b44; color: #f5a0a0; }

/* RESPONSIVE */
@media (max-width: 900px) {
  .contact-layout  { grid-template-columns: 1fr; gap: 3rem; }
  .about-layout    { grid-template-columns: 1fr; gap: 3rem; }
  .engagement-grid { grid-template-columns: 1fr; }
  .engagement-card { border-right: none; border-bottom: 2px solid var(--navy-mid); }
  .engagement-card:last-child { border-bottom: none; }
}
@media (max-width: 768px) {
  section { padding: 4rem 1.5rem; }
  .hero { padding: 8rem 1.5rem 0; }
  .services-grid { grid-template-columns: 1fr; }
  .service-card { border-right: none; border-bottom: 2px solid var(--periwinkle); }
  .service-card:last-child { border-bottom: none; }
  .form-row { grid-template-columns: 1fr; }
  .contact-section { padding: 4rem 1.5rem; }
}
@media (prefers-reduced-motion: reduce) {
  *, .hero-eyebrow, .hero-headline, .hero-sub, .hero-actions {
    animation: none !important;
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
  }
  .reveal { opacity: 1; transform: none; }
}
</style>
